import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { eventBus } from '@/utils/eventBus';
import { log } from '@/utils/vueLogger.js';
import { i18n } from './index';
import { buildOverlay } from './translationOverlay';

// Vue is the source of truth for translations. The canonical dictionary lives on the
// PARENT window (window.__vueI18nStore), which the legacy $T() shim pulls from via
// window.parent — this is reload-proof (the legacy iframe navigates via form.submit /
// location.reload, creating fresh documents that would otherwise lose pushed data).
// We additionally push into the current iframe document and bump the KnockoutJS
// `translationsVersion` observable — the legacy equivalent of valueHasMutated() that
// forces already-rendered $T() bindings (and Kendo labels) to re-evaluate.

let _currentLocale = null;
let _reloadHandlerInstalled = false;

function getStore() {
	if (!window.__vueI18nStore) window.__vueI18nStore = {};
	return window.__vueI18nStore;
}

function getIframeWindow() {
	const win = iframeDataBridge.getIframeWindow();
	return win && win.externis ? win : null;
}

/**
 * Replay imperative md-* labels that were rendered once via `data-translation`
 * (these are not KO text bindings, so the version bump alone won't refresh them).
 */
function replayDataTranslations(win) {
	try {
		const $ = win.$ || win.jQuery;
		if (!$) return;
		$('[data-translation-done="true"][data-translation]', win.document).each(function () {
			if ('label' in this) {
				try {
					this.label = win.eval($(this).attr('data-translation'));
				} catch {
					/* ignore individual binding errors */
				}
			}
		});

		if (typeof win.$T === 'function') {
			$('[data-i18n-key]', win.document).each(function () {
				const key = this.getAttribute('data-i18n-key');
				if (!key) return;
				try {
					this.textContent = win.$T(key);
				} catch {
					/* ignore individual errors */
				}
			});
		}
	} catch {
		/* jQuery not ready */
	}
}

/**
 * Push the merged (base + applicationResources overlay) dictionary for `locale`
 * into the iframe and trigger a legacy re-render.
 * @param {string} locale
 * @param {Array} [rawResources] raw applicationResources array, kept on
 *        externis.applicationResources[locale] for legacy consumers (e.g. botng lexicon).
 */
export function pushTranslationsToIframe(locale, rawResources) {
	_currentLocale = locale;

	// 1) Always update the canonical parent-window store (always available, reload-proof).
	//    Plain snapshot — getLocaleMessage returns a reactive proxy.
	const flat = { ...(i18n.global.getLocaleMessage(locale) || {}) };
	const store = getStore();
	store[locale] = flat;

	// Re-push automatically whenever the legacy iframe (re)loads a fresh document.
	installReloadHandler();

	// 2) Best-effort push into the current iframe document + KO re-render.
	const win = getIframeWindow();
	if (!win) {
		// Not ready yet — flush once it announces readiness (idempotent registration).
		eventBus.off('externis-ready', _onExternisReady);
		eventBus.on('externis-ready', _onExternisReady);
		return;
	}

	const externis = win.externis;

	if (!externis.__vueTranslations) externis.__vueTranslations = {};
	externis.__vueTranslations[locale] = flat;

	// Preserve the raw resources array for legacy consumers that parse name/value/W- entries.
	if (Array.isArray(rawResources)) {
		if (!externis.applicationResources || typeof externis.applicationResources !== 'object') externis.applicationResources = {};
		externis.applicationResources[locale] = rawResources;
	}

	// Ensure the KO observable exists (created with the iframe's own ko instance).
	try {
		if (!externis.translationsVersion && win.ko && typeof win.ko.observable === 'function') {
			externis.translationsVersion = win.ko.observable(0);
		}
		if (typeof externis.translationsVersion === 'function') {
			externis.translationsVersion(externis.translationsVersion() + 1);
		}
	} catch (e) {
		log('warn', 'i18n', `Could not bump translationsVersion: ${e.message}`);
	}

	replayDataTranslations(win);
	log('success', 'i18n', `Pushed ${Object.keys(flat).length} translations to iframe for ${locale}`);
}

function _onExternisReady() {
	if (_currentLocale) pushTranslationsToIframe(_currentLocale);
}

// Re-push the current locale each time the legacy iframe document loads, so that
// freshly navigated/reloaded documents (portal, post-sync, login) get the bump.
function installReloadHandler() {
	if (_reloadHandlerInstalled) return;
	const iframe = iframeDataBridge.getIframe();
	if (!iframe) return;
	_reloadHandlerInstalled = true;
	iframe.addEventListener('load', () => {
		if (_currentLocale) {
			// Defer so the iframe's externis/ko have time to initialize.
			setTimeout(() => pushTranslationsToIframe(_currentLocale), 300);
		}
	});
}

/**
 * Merge an applicationResources payload into vue-i18n (overriding the base, so
 * priority becomes custom > default > base) and propagate to the legacy iframe.
 */
export function applyTranslations(locale, resources) {
	const overlay = buildOverlay(resources);
	if (Object.keys(overlay).length > 0) {
		i18n.global.mergeLocaleMessage(locale, overlay);
	}
	pushTranslationsToIframe(locale, Array.isArray(resources) ? resources : undefined);
}
